import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { projects } from '@/components/portfolio/Projects';
import { CaseStudyThumb } from '@/components/portfolio/CaseStudyThumb';
import { PORTFOLIO } from '@/constants/testIds';
import { useCaseStudyVisibility } from '@/hooks/useCaseStudyVisibility';
import { Seo, SITE_URL } from '@/lib/seo';

// Full listing view for every case study — reached from the "View All
// Projects" links on the Home page. Previously those pointed to a dead
// himanshutanty.com URL; this in-app page renders the entire portfolio grid
// with the same typographic thumbnails used on the homepage carousel.

const WorkIndex = () => {
    const { isVisible } = useCaseStudyVisibility();
    const list = useMemo(
        () => projects.filter((p) => isVisible(p.slug)),
        [isVisible]
    );
    return (
        <>
            <Seo
                title="Selected Work · Case Studies by Himanshu Tanty"
                description="Enterprise dashboards, mobile products, brand systems and design-thinking case studies from my product design work."
                canonical={`${SITE_URL}/work`}
            />
        <section
            data-testid="work-index"
            className="pt-24 md:pt-28 pb-16 md:pb-24 bg-[var(--bg)]"
        >
            <div className="max-w-[1240px] mx-auto px-4 md:px-8">
                {/* Header */}
                <div className="flex items-end justify-between gap-6 flex-wrap border-b border-[var(--line)] pb-8 md:pb-12">
                    <div>
                        <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                            <span className="tabular-nums text-[var(--ink-3)]">
                                Index · Selected work
                            </span>
                        </div>
                        <h1 className="mt-3 font-display leading-[0.9] text-5xl md:text-7xl text-[var(--ink)]">
                            Every{' '}
                            <span className="font-display-italic text-[var(--accent)]">
                                project
                            </span>
                            <span className="text-[var(--ink-2)]">.</span>
                        </h1>
                        <p className="mt-4 font-sans-brand text-[13.5px] leading-[1.6] text-[var(--ink-2)] max-w-[62ch]">
                            A single view of everything I&rsquo;ve shipped:
                            enterprise dashboards, mobile products, brand
                            systems, and design-thinking case studies. Click
                            any tile to open the write-up.
                        </p>
                    </div>
                    <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)] tabular-nums">
                        {list.length} projects
                    </div>
                </div>

                {/* Grid */}
                <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {list.map((p, i) => (
                        <motion.div
                            key={p.slug}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-8%' }}
                            transition={{
                                duration: 0.55,
                                delay: (i % 3) * 0.06,
                                ease: [0.2, 0.8, 0.2, 1],
                            }}
                        >
                            <Link
                                to={`/work/${p.slug}`}
                                data-testid={PORTFOLIO.projectCard(p.slug)}
                                data-cursor="hover"
                                className="group block"
                            >
                                <div className="relative overflow-hidden rounded-2xl border border-[var(--line)] group-hover:border-[var(--line-2)] transition-colors aspect-[4/3]">
                                    <CaseStudyThumb
                                        slug={p.slug}
                                        className="w-full h-full"
                                    />
                                </div>
                                <div className="mt-4 flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)] tabular-nums">
                                            {p.category}
                                        </div>
                                        <div className="mt-1.5 font-display text-xl md:text-2xl text-[var(--ink)] leading-tight truncate">
                                            {p.title}
                                        </div>
                                    </div>
                                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] group-hover:bg-[var(--accent)] group-hover:text-[var(--accent-ink)] group-hover:border-transparent group-hover:-rotate-45 transition-all shrink-0">
                                        <ArrowUpRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
        </>
    );
};

export default WorkIndex;
